import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { findActiveItem } from "./tableOfContents";
import {
  isTabClickLocked,
  setActiveItem,
} from "../../features/tableOfContents/TableOfContentsSlice";
import useScreenSize from "../useScreenSize";

const HEADING_SELECTORS = "h2.topicontainer, h3.dartsectiondisplay, h4.dartsectiondisplay";
const ACTIVE_HEADING_THRESHOLD_PX = 100; // Distance from top to consider heading "active"
const TABLET_SCROLL_CONTAINER_SELECTOR = ".rightColumn";

/**
 * Hook to manage Table of Contents highlighting based on scroll position.
 * Active item selection on navigation is handled data-driven by useTableOfContentsItems
 * (via the activeNode returned from generateTocForCurrentPath).  This hook only
 * updates the active item in response to the user manually scrolling.
 */
export function useScrollActiveTocItem() {
  const dispatch = useDispatch();
  const { tableOfContents } = useSelector((state) => state.tableOfContents);
  const { isTabletSize } = useScreenSize();

  const onScroll = useCallback(
    (event) => {
      if (isTabClickLocked()) return;

      const activeHeading = findActiveHeading(event.target);
      if (activeHeading) {
        updateActiveTocItem(activeHeading, tableOfContents, dispatch);
      }
    },
    [dispatch, tableOfContents],
  );

  useTabletScrollListener(isTabletSize, onScroll);

  return { onScroll, isTabletSize };
}

function findActiveHeading(scrollContainer) {
  const headings = document.querySelectorAll(HEADING_SELECTORS);
  if (headings.length === 0) return null;

  const containerTop = scrollContainer.getBoundingClientRect().top;

  const visibleHeading = findClosestVisibleHeading(headings, containerTop);
  if (visibleHeading) return visibleHeading;

  const headingAbove = findLastHeadingAbove(headings, containerTop);
  if (headingAbove) return headingAbove;

  // Do NOT fall back to headings[0]. When the container is at scrollTop=0
  // (page load, resetState, scroll restoration) all headings are below the
  // viewport — returning headings[0] here would reset activeItem to
  // children[0] and override the data-driven selection from useTableOfContentsItems.
  return null;
}

function findClosestVisibleHeading(headings, containerTop) {
  let closestHeading = null;
  let minDistance = Infinity;

  headings.forEach((heading) => {
    const distanceFromTop = heading.getBoundingClientRect().top - containerTop;

    if (distanceFromTop >= 0 && distanceFromTop < ACTIVE_HEADING_THRESHOLD_PX) {
      if (distanceFromTop < minDistance) {
        minDistance = distanceFromTop;
        closestHeading = heading;
      }
    }
  });

  return closestHeading;
}

function findLastHeadingAbove(headings, containerTop) {
  let lastHeading = null;

  headings.forEach((heading) => {
    if (heading.getBoundingClientRect().bottom < containerTop) {
      lastHeading = heading;
    }
  });

  return lastHeading;
}

function updateActiveTocItem(activeHeading, tableOfContents, dispatch) {
  const lookupKey = extractTocLookupKey(activeHeading);
  const activeItem = findActiveItem(tableOfContents, lookupKey);

  if (activeItem) {
    dispatch(setActiveItem(activeItem));
  }
}

function extractTocLookupKey(heading) {
  return heading.hasAttribute("filePath")
    ? { filePath: heading.getAttribute("filePath") }
    : { title: heading.textContent.trim().toLowerCase() };
}

function useTabletScrollListener(isTabletSize, onScroll) {
  useEffect(() => {
    if (!isTabletSize) return;

    const scrollElement = document.querySelector(TABLET_SCROLL_CONTAINER_SELECTOR);
    if (!scrollElement) return;

    const handleScroll = () => onScroll({ target: scrollElement });

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [isTabletSize, onScroll]);
}
